/**
 * Delivery Control Plane - Pure Mapping Logic
 * 
 * Translates reconciliation recommendations into concrete,
 * proportional control signals for downstream systems.
 * 
 * This module is pure and stateless - it only transforms data.
 * All values are clamped and deterministic.
 */

import type { ReconciliationResult } from './deliveryReconciliation';

// --- Control Signal Types ---

export interface ControlSignals {
  /** Multiplier for retry priority (1.0 = normal, >1.0 = higher priority) */
  retryPriorityMultiplier: Map<string, number>;
  /** Boost for pool inclusion probability (0 = no boost, 0.5 = 50% more likely) */
  poolInclusionBoost: Map<string, number>;
  /** Temporary guaranteed minimum overrides */
  guaranteedMinimumOverrides: Map<string, number>;
  /** Pacing caps to slow down over-delivery (1.0 = normal, <1.0 = slower) */
  pacingCaps: Map<string, number>;
  /** System-level warnings */
  systemWarnings: SystemWarning[];
  /** Timestamp when signals were computed */
  computedAt: number;
}

export interface SystemWarning {
  type: 'INFEASIBLE_CONTRACT' | 'OVERLOAD' | 'CONFLICTING_SIGNALS' | 'SYSTEM_CAPACITY';
  agentId?: string;
  contractId?: string;
  message: string;
  severity: 'info' | 'warn' | 'critical';
}

// --- Configuration ---

export interface ControlConfig {
  /** Min/max for retry priority multiplier */
  retryPriorityRange: { min: number; max: number };
  /** Min/max for pool inclusion boost */
  poolBoostRange: { min: number; max: number };
  /** Min/max for pacing cap */
  pacingCapRange: { min: number; max: number };
  /** Max guaranteed minimum increase factor */
  maxGuaranteedBoostFactor: number;
}

export const DEFAULT_CONTROL_CONFIG: ControlConfig = {
  retryPriorityRange: { min: 1.0, max: 1.6 },
  poolBoostRange: { min: 0, max: 0.5 },
  pacingCapRange: { min: 0.7, max: 1.0 },
  maxGuaranteedBoostFactor: 1.5,
};

// --- Utility Functions ---

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Map urgency score (0-1) to retry priority multiplier
 * Higher urgency = higher multiplier
 */
function mapUrgencyToRetryMultiplier(
  urgencyScore: number,
  config: ControlConfig
): number {
  const { min, max } = config.retryPriorityRange;
  // Linear mapping from urgency to multiplier
  return min + (max - min) * urgencyScore;
}

/**
 * Map recommended boost to clamped pool inclusion boost
 */
function mapRecommendedBoostToPoolBoost(
  recommendedBoost: number,
  config: ControlConfig
): number {
  return clamp(recommendedBoost, config.poolBoostRange.min, config.poolBoostRange.max);
}

/**
 * Map recommended pacing reduction to clamped pacing cap
 */
// --- Main Control Signal Computation ---

/**
 * Compute control signals from reconciliation results
 * 
 * This is the main entry point for the control plane.
 * It takes pure reconciliation data and produces pure control signals.
 */
export function computeControlSignals(
  reconciliationResult: ReconciliationResult | null,
  config: ControlConfig = DEFAULT_CONTROL_CONFIG
): ControlSignals {
  const signals: ControlSignals = {
    retryPriorityMultiplier: new Map(),
    poolInclusionBoost: new Map(),
    guaranteedMinimumOverrides: new Map(),
    pacingCaps: new Map(),
    systemWarnings: [],
    computedAt: Date.now(),
  };

  if (!reconciliationResult) {
    return signals;
  }

  const { systemRecommendations, contractEvaluations, summary } = reconciliationResult;

  // --- Process Retry Priority Recommendations ---
  for (const rec of systemRecommendations.increaseRetryPriority) {
    const multiplier = mapUrgencyToRetryMultiplier(rec.urgencyScore, config);
    
    // Aggregate: take max if multiple contracts for same agent
    const existing = signals.retryPriorityMultiplier.get(rec.agentId) ?? 1.0;
    signals.retryPriorityMultiplier.set(rec.agentId, Math.max(existing, multiplier));
  }

  // --- Process Pool Inclusion Recommendations ---
  for (const rec of systemRecommendations.expandPoolInclusion) {
    const boost = mapRecommendedBoostToPoolBoost(rec.recommendedBoost, config);
    
    // Aggregate: sum boosts (clamped)
    const existing = signals.poolInclusionBoost.get(rec.agentId) ?? 0;
    const total = clamp(existing + boost, config.poolBoostRange.min, config.poolBoostRange.max);
    signals.poolInclusionBoost.set(rec.agentId, total);
  }

  // --- Process Guaranteed Minimum Boost Recommendations ---
  for (const rec of systemRecommendations.boostGuaranteedMinimum) {
    // recommendedBoostIncrease is a ratio (0-1), we use it as the override factor
    // Since we don't have currentGuaranteed in the recommendation, we use the boost directly
    const boostFactor = clamp(1 + rec.recommendedBoostIncrease, 1, config.maxGuaranteedBoostFactor);
    
    // Take max if multiple recommendations
    const existing = signals.guaranteedMinimumOverrides.get(rec.agentId) ?? 1.0;
    signals.guaranteedMinimumOverrides.set(rec.agentId, Math.max(existing, boostFactor));
  }

  // --- Process Pacing Reduction Recommendations ---
  for (const rec of systemRecommendations.reducePacing) {
    // excessDelivered tells us how much to slow down
    // Map excess to a pacing cap: more excess = lower cap
    const excessRatio = Math.min(rec.excessDelivered / 10, 1); // Normalize excess
    const cap = clamp(1.0 - (excessRatio * 0.3), config.pacingCapRange.min, config.pacingCapRange.max);
    
    // Take minimum cap if multiple recommendations
    const existing = signals.pacingCaps.get(rec.agentId) ?? 1.0;
    signals.pacingCaps.set(rec.agentId, Math.min(existing, cap));
  }

  // --- Process Warnings ---
  for (const warning of systemRecommendations.warnings) {
    // Warnings have agentIds (array) not agentId (single)
    const firstAgentId = warning.agentIds?.[0];
    const firstContractId = warning.contractIds?.[0];
    
    signals.systemWarnings.push({
      type: warning.type as SystemWarning['type'],
      agentId: firstAgentId,
      contractId: firstContractId,
      message: warning.message,
      severity: mapWarningSeverity(warning.type),
    });
  }

  // --- Check for Infeasible Contracts ---
  if (summary.infeasibleCount > 0) {
    const infeasibleContracts = contractEvaluations.filter(e => !e.isFeasible);
    for (const contract of infeasibleContracts) {
      signals.systemWarnings.push({
        type: 'INFEASIBLE_CONTRACT',
        agentId: contract.agentId,
        contractId: contract.contractId,
        message: `Contract ${contract.contractId} is mathematically impossible to fulfill. Gap: ${contract.deliveryGap} leads, Days remaining: ${contract.daysRemaining}`,
        severity: 'critical',
      });
    }
  }

  // --- Check for System Overload ---
  const totalAtRisk = summary.atRisk + summary.behind;
  const riskRatio = totalAtRisk / Math.max(summary.activeContracts, 1);
  
  if (riskRatio > 0.5) {
    signals.systemWarnings.push({
      type: 'SYSTEM_CAPACITY',
      message: `${Math.round(riskRatio * 100)}% of active contracts are behind or at risk. System may be overloaded.`,
      severity: riskRatio > 0.7 ? 'critical' : 'warn',
    });
  }

  // --- Detect Conflicting Signals ---
  // Check for agents with both boost and cap
  for (const agentId of signals.poolInclusionBoost.keys()) {
    if (signals.pacingCaps.has(agentId)) {
      const boost = signals.poolInclusionBoost.get(agentId)!;
      const cap = signals.pacingCaps.get(agentId)!;
      
      if (boost > 0.2 && cap < 0.9) {
        signals.systemWarnings.push({
          type: 'CONFLICTING_SIGNALS',
          agentId,
          message: `Agent ${agentId} has both pool boost (${(boost * 100).toFixed(0)}%) and pacing cap (${(cap * 100).toFixed(0)}%). Review contract mix.`,
          severity: 'warn',
        });
      }
    }
  }

  return signals;
}

function mapWarningSeverity(type: string): 'info' | 'warn' | 'critical' {
  switch (type) {
    case 'INFEASIBLE_DEMAND':
      return 'critical';
    case 'HIGH_RISK_RATIO':
      return 'warn';
    default:
      return 'info';
  }
}

// --- Helper Functions for Downstream Systems ---

/**
 * Get retry priority multiplier for a specific agent
 * Returns 1.0 if no signal exists
 */
export function getRetryPriorityMultiplier(
  signals: ControlSignals,
  agentId: string
): number {
  return signals.retryPriorityMultiplier.get(agentId) ?? 1.0;
}

/**
 * Get pool inclusion boost for a specific agent
 * Returns 0 if no signal exists
 */
export function getPoolBoost(
  signals: ControlSignals,
  agentId: string
): number {
  return signals.poolInclusionBoost.get(agentId) ?? 0;
}

/**
 * Get guaranteed minimum override for a specific agent
 * Returns undefined if no override exists
 */
export function getGuaranteedMinOverride(
  signals: ControlSignals,
  agentId: string
): number | undefined {
  return signals.guaranteedMinimumOverrides.get(agentId);
}

/**
 * Get pacing cap for a specific agent
 * Returns 1.0 if no cap exists
 */
export function getPacingCap(
  signals: ControlSignals,
  agentId: string
): number {
  return signals.pacingCaps.get(agentId) ?? 1.0;
}

/**
 * Check if any critical warnings exist
 */
export function hasCriticalWarnings(signals: ControlSignals): boolean {
  return signals.systemWarnings.some(w => w.severity === 'critical');
}

/**
 * Get all agents with active control signals
 */
export function getAgentsWithActiveSignals(signals: ControlSignals): string[] {
  const agentIds = new Set<string>();
  
  for (const [agentId, multiplier] of signals.retryPriorityMultiplier) {
    if (multiplier > 1.0) agentIds.add(agentId);
  }
  
  for (const [agentId, boost] of signals.poolInclusionBoost) {
    if (boost > 0) agentIds.add(agentId);
  }
  
  for (const agentId of signals.guaranteedMinimumOverrides.keys()) {
    agentIds.add(agentId);
  }
  
  for (const [agentId, cap] of signals.pacingCaps) {
    if (cap < 1.0) agentIds.add(agentId);
  }
  
  return Array.from(agentIds);
}

/**
 * Serialize control signals for logging/persistence
 */
export function serializeControlSignals(signals: ControlSignals): Record<string, unknown> {
  return {
    computedAt: signals.computedAt,
    retryPriorityMultiplier: Object.fromEntries(signals.retryPriorityMultiplier),
    poolInclusionBoost: Object.fromEntries(signals.poolInclusionBoost),
    guaranteedMinimumOverrides: Object.fromEntries(signals.guaranteedMinimumOverrides),
    pacingCaps: Object.fromEntries(signals.pacingCaps),
    systemWarnings: signals.systemWarnings,
    activeAgentCount: getAgentsWithActiveSignals(signals).length,
    hasCriticalWarnings: hasCriticalWarnings(signals),
  };
}
